// `refund.issued.v1` — emitted by refund-backend (Python) when a refund is
// issued against a donation, AFTER the transaction row's `state` has been
// overwritten with the refund type and the `refunded` list appended.
//
// Producer: refund-backend (`Source: 'charitystack.refunds'`,
//   functions/product_analytics_events.py::publish_refund_issued), called from
//   the Stripe AND Finix refund handlers. NOT emitted by the PayPal refund
//   path (known gap — the reconcile cron covers PayPal refunds within one
//   cycle). Originally built as a product-analytics event; PE-1768 makes it a
//   consumed domain event. The producer `_compact()`s the detail — null fields
//   are DROPPED, hence the optionals below. `originIdempotencyKey` is
//   `refund.issued:<processor>:<refundId>` (unique per refund, so a second
//   partial refund on the same donation is a distinct event — correct: each
//   refund warrants its own recompute).
// Consumer: marketing-backend — the revenue-reversal bridge → the
//   `revenue-reversal` Inngest workflow, which recomputes the credited
//   broadcast's "$ Raised" rollup (PE-1665) so refunds reflect in seconds
//   instead of one reconcile-cron cycle.
// detailType in EventBridge: `refund.issued.v1` (Commitment 6).
//
// Status: active
//
// Parsed NON-strictly (no .strict()) — the producer may add fields without
// coordinating a schema bump (same posture as donation.completed.v1).
import { z } from 'zod';

import { eventEnvelopeSchema } from './base.js';

export const refundIssuedV1Schema = eventEnvelopeSchema.extend({
  // The refunded donation's per-charge transaction id — the consumer's only
  // required handle (donations-index lookup → credited broadcast).
  transactionId: z.string().min(1),
  refundId: z.string().min(1),
  // Refund amount in integer CENTS. Optional defensively (the producer
  // compacts nulls); the consumer recomputes from source and never uses it
  // for arithmetic.
  amountCents: z.number().int().optional(),
  // FULL_REFUND | PARTIAL_REFUND | FEE_REFUND | FUND_REFUND | TICKET_REFUND —
  // kept as a plain string: the consumer recomputes regardless of type, and a
  // new refund type must not break parsing.
  refundType: z.string().optional(),
  refundReason: z.string().optional(),
  currency: z.string().optional(),
  paymentProcessor: z.string().optional(),
});

export type RefundIssuedV1 = z.infer<typeof refundIssuedV1Schema>;

export const REFUND_ISSUED_V1_DETAIL_TYPE = 'refund.issued.v1' as const;
export const REFUND_ISSUED_V1_SOURCE = 'charitystack.refunds' as const;
